'use client';

import './gallery-cinema.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.ts';

import { Image } from '../../../atoms/image/image.tsx';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselLazyLoadImage,
  CarouselNext,
  CarouselPrevious,
} from '../../../molecules/carousel/carousel.tsx';
import { FeatureCard } from '../../../molecules/feature-card/feature-card.tsx';
import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import {
  Lightbox,
  LightboxTrigger,
  LightboxVideoSchemas,
} from '../../../molecules/lightbox/lightbox.tsx';
import { getPlaceholderItems } from '../../../utils/storybook.tsx';
import { getBaseSizeProps } from '../../../utils/text-utils.tsx';
import { JumbotronNotable } from '../../jumbotron/notable/jumbotron-notable.tsx';

export const GalleryCinema: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  items,
  alt,
  ...props
}) => {
  const { t } = useTranslation(['skippy']);

  const hasHeaderContent = !!props.title || !!props.body;
  const hasHeaderCard = hasHeaderContent && media?.image;
  const hasHeaderGroup = hasHeaderContent && !media?.image;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const galleryItems =
    !items || items.length < 1 ? getPlaceholderItems(items, 1) : items;

  const hasBodyItems = galleryItems.some((item) => !!item.body);

  // A gallery at the top of the page holds the LCP image, so its first image
  // must render server-side rather than waiting for hydration.
  const isPrimary = seoLevel === 1;

  const soloItem = galleryItems.length === 1 ? galleryItems[0] : undefined;

  // A single still image needs neither a carousel nor a lightbox. Rendering the
  // Image atom directly puts it in the SSR output, where the preload scanner
  // can find it. Video still needs the lightbox to play in.
  const soloImage =
    soloItem && !hasBodyItems && !soloItem.media?.video
      ? soloItem.media
      : undefined;

  return (
    <section
      className={cn('GalleryCinema component', { 'GalleryCinema--alt': alt })}
    >
      <div className="GalleryCinema__container container flex flex-col gap-comfy-xl lg:gap-roomy-xs">
        {hasHeaderCard && (
          <FeatureCard
            {...props}
            {...getBaseSizeProps(headerDisplayLevel)}
            seoLevel={seoLevel}
            media={media}
            imagePlacement="right"
            variant="horizontal"
            inline
          />
        )}
        {hasHeaderGroup && <HeaderGroup {...props} seoLevel={seoLevel} />}

        {soloImage && (
          <figure className="GalleryCinema__solo m-none">
            <div className="GalleryCinema__image">
              <Image
                image={soloImage.image}
                aspectRatio="16-9"
                rounded="lg"
                hasPlaceholder
                priority={isPrimary}
                sizeIntent="full"
              />
            </div>
            {soloImage.caption && (
              <figcaption className="GalleryCinema__attribution label label-lg mt-comfy-sm">
                {soloImage.caption}
              </figcaption>
            )}
          </figure>
        )}

        {!soloImage && (
          <Carousel
            aria-label={t('skippy:gallery.title', {
              title: props.title,
            })}
            opts={{
              align: 'start',
              loop: false,
              watchFocus: false,
            }}
          >
            <LightboxVideoSchemas items={galleryItems} />
            <div className="GalleryCinema__wrapper flex flex-col gap-comfy-lg">
              <div
                className={cn('GalleryCinema__carousel', {
                  'has-body': hasBodyItems,
                })}
                data-length={galleryItems.length}
              >
                <CarouselContent>
                  {galleryItems.map((slide, index) => (
                    <CarouselItem
                      key={['slide', index, slide.title].join('-')}
                      index={index}
                      className="GalleryCinema__slide"
                      aria-label={`${t('skippy:gallery.slide.title', {
                        index: index + 1,
                        length: galleryItems.length,
                      })}${slide.title && ` - ${slide.title}`}`}
                    >
                      <figure className="m-none">
                        <Lightbox
                          key={['lightbox', index, slide.title].join('-')}
                          items={galleryItems}
                          startIndex={index}
                        >
                          <LightboxTrigger
                            className="Lightbox__trigger  w-full"
                            ariaLabel={t('skippy:gallery.open')}
                          >
                            <>
                              {!hasBodyItems && (
                                <div className="GalleryCinema__image">
                                  <CarouselLazyLoadImage
                                    index={index}
                                    media={slide.media}
                                    isVideoThumbnail={!!slide.media?.video}
                                    // Only the first slide is above the fold;
                                    // Only the first slide is above the fold;
                                    // the rest stay deferred.
                                    priority={isPrimary && index === 0}
                                  />
                                </div>
                              )}

                              {hasBodyItems && (
                                <JumbotronNotable
                                  seoLevel={
                                    hasHeaderContent ? seoLevel + 1 : seoLevel
                                  }
                                  {...{ ...slide, alt, inline: true }}
                                />
                              )}
                            </>
                          </LightboxTrigger>
                        </Lightbox>

                        {slide.media?.caption && (
                          <figcaption className="GalleryCinema__attribution label label-lg mt-comfy-sm">
                            {slide.media.caption || slide.title}
                          </figcaption>
                        )}
                      </figure>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </div>
              {galleryItems.length > 1 && (
                <div className="GalleryCinema__arrows flex gap-comfy-sm items-center justify-end">
                  <CarouselPrevious />
                  <CarouselNext />
                </div>
              )}
            </div>
          </Carousel>
        )}
      </div>
    </section>
  );
};
